// pages/SignInPage.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import type { EducationLevel } from '../types/user';

export default function SignInPage() {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  
  // Profile form states
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [currentEducation, setCurrentEducation] = useState<EducationLevel>('Graduation');

  const sendOtp = useAuthStore((state) => state.sendOtp);
  const verifyOtp = useAuthStore((state) => state.verifyOtp);
  const createUserProfile = useAuthStore((state) => state.createUserProfile);
  const needsProfileCreation = useAuthStore((state) => state.needsProfileCreation);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  useEffect(() => {
    authService.setupRecaptcha('recaptcha-container');
  }, []);

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendOtp(phone);
    const currentError = useAuthStore.getState().error;
    if (!currentError) {
      setOtpSent(true);
    } else {
      authService.resetRecaptcha('recaptcha-container');
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    await verifyOtp(otp);
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createUserProfile({ displayName, email, currentEducation });
  };

  const renderProfileForm = () => (
    <form onSubmit={handleProfileSubmit} className="space-y-4 animate-fadeIn">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
        <input
          id="name"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
        />
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
        />
      </div>
      <div>
        <label htmlFor="education" className="block text-sm font-medium text-gray-700">Current Education</label>
        <select
          id="education"
          value={currentEducation}
          onChange={(e) => setCurrentEducation(e.target.value as EducationLevel)}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
        >
          <option value="School">School</option>
          <option value="12th">12th</option>
          <option value="Diploma">Diploma</option>
          <option value="Graduation">Graduation</option>
          <option value="Post Graduation">Post Graduation</option>
          <option value="Doctorate">Doctorate</option>
          <option value="Others">Others</option>
        </select>
      </div>
      <button
        type="submit"
        disabled={isLoading || !displayName || !email}
        className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 mt-4"
      >
        {isLoading ? 'Saving...' : 'Complete Profile'}
      </button>
    </form>
  );

  return (
    <div className="flex items-center justify-center min-h-[80vh]">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md relative">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-sm font-medium"
        >
          Skip →
        </button>

        <h1 className="text-2xl font-bold text-center mb-2 text-primary">CareerNova Learning</h1>
        <p className="text-gray-600 text-center mb-6">
          {needsProfileCreation ? 'Complete your profile to continue' : 'Sign in with your phone number'}
        </p>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div id="recaptcha-container" className={needsProfileCreation ? 'hidden' : ''}></div>

        {needsProfileCreation ? (
          renderProfileForm()
        ) : !otpSent ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number</label>
              <div className="flex mt-1">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">+91</span>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit number"
                  required
                  className="flex-1 block px-3 py-2 border border-gray-300 rounded-r-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading || phone.length !== 10}
              className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700">Enter OTP</label>
              <input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit OTP"
                required
                maxLength={6}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || otp.length !== 6}
              className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <button
              type="button"
              onClick={() => {
                setOtp('');
                useAuthStore.getState().clearError();
                setOtpSent(false);
                authService.resetRecaptcha('recaptcha-container');
              }}
              className="w-full text-primary hover:underline"
            >
              Change Phone Number
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
